import { FC, useState } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Grid, Tooltip, Box } from '@mui/material';

import { SCUIContent } from '../../atomic/organisms';
import { SCUILoadingLinear } from '../../atomic/atoms';

import { SCUICustomDataGrid, SCUITextFieldWithSelectBox } from '../../atomic/molecules';
import { CustomerCommand } from '../../services/command';
import { LcwMoneyQuery } from '../../services/query';
import TimeHelper from '../../utils/time';
import { CAN, canCls } from './customerAnonymization.classes';

export const CustomerAnonymization: FC<any> = () => {
    const [customer, setCustomer] = useState<any>('');
    const [documentType, setDocumentType] = useState(1);
    const [searchedText, setSearchedText] = useState('');
    const [pageSize, setPageSize] = useState(5);
    const [pageLoading, setPageLoading] = useState(false);
    const [grid, setGrid] = useState(false);

    const { t } = useTranslation();

    const handleSearchClick = (hideError: boolean = false) => {
        if (documentType === 1) {
            GetCustomerInfoByPhoneOnSSO(hideError);
        } else if (documentType === 2) {
            GetCustomerWithEmail(hideError);
        }
    };

    const GetCustomerWithEmail = async (hideError: boolean = false) => {
        setPageLoading(true);
        setGrid(true);
        LcwMoneyQuery.GetCustomerIdWithEmail(searchedText.toString().trim())
            .then((res: any) => {
                if (res.data.customer !== null) {
                    let data = {
                        customerId: res.data.customer.customerId || ' - ',
                        platform: res.data.customer.platform || ' - ',
                        name: res.data.customer.firstName || ' - ',
                        surname: res.data.customer.lastName || ' - ',
                        lastLoginDate: res.data.customer.lastLoginDate || ' - ',
                        birthDate: res.data.customer.dateOfBirth,
                        phoneNumber: res.data.customer.phoneNumber || ' - ',
                        email: res.data.customer.email || ' - ',
                        customerType: res.data.customer.customerType || ' - ',
                    };
                    setCustomer(data);
                } else {
                    if (!hideError) {
                        Swal.fire(t('general.error'), t('customerInformation.customerNotFound'), 'info');
                    }
                    setCustomer(null);
                }
            })
            .finally(() => {
                setPageLoading(false);
            });
    };

    const UpdateAnonymization = () => {
        Swal.fire({
            text: t('customerAnonymization.anonymizeQuestion'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                let params: any = {};
                params.customerId = customer.customerId;

                CustomerCommand.CustomerAnonymize(params)
                    .then((response: any) => {
                        if (response.isAnonymized === true) {
                            handleSearchClick(true);
                            Swal.fire(t('customerAnonymization.anonymized'), '', 'success');
                        }
                    })
                    .catch(() => {
                        Swal.fire(t('general.anErrorOccured'), '', 'error');
                    });
            }
        });
    };

    const GetCustomerInfoByPhoneOnSSO = async (hideError: boolean = false) => {
        setPageLoading(true);
        setGrid(true);
        CustomerCommand.GetCustomerInfoByPhoneOnSSO(modifyPhoneNumber(searchedText.toString().trim()))
            .then((res: any) => {
                if (!res.data?.isSucceed) {
                    Swal.fire(t('general.error'), t('customerInformation.customerNotFound'), 'error');
                    setCustomer(null);
                } else if (res.data !== null) {
                    let data = {
                        customerId: res.data.customerId || ' - ',
                        platform: res.data.platform || ' - ',
                        name: res.data.name || ' - ',
                        lastname: res.data.lastname || ' - ',
                        lastLoginDate: res.data.lastLoginDate || ' - ',
                        birthDate: res.data.birthDate,
                        phoneNumber: res.data.phoneNumber || ' - ',
                        email: res.data.email || ' - ',
                        customerType: res.data.customerType || ' - ',
                    };
                    setCustomer(data);
                } else {
                    if (!hideError) {
                        Swal.fire(t('general.error'), t('customerInformation.customerNotFound'), 'error');
                    }
                    setCustomer(null);
                }
            })
            .finally(() => {
                setPageLoading(false);
            });
    };

    const modifyPhoneNumber = (phoneNumber: any) => {
        if (!phoneNumber.startsWith('0')) {
            return '0090' + phoneNumber;
        }
        if (phoneNumber.startsWith('0090')) {
            return phoneNumber;
        }
        return '0090' + phoneNumber.slice(1);
    };

    return (
        <SCUIContent className={`${CAN.mainPage} `}>
            <SCUILoadingLinear display={pageLoading} />
            <Grid
                className={CAN.mainTitle}
                container
                sx={{ marginBottom: '2rem' }}
                size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <h2>{t('customerAnonymization.pageTitle')}</h2>
            </Grid>
            <Grid
                className={CAN.mainSearch}
                size={{
                    xl: 12,
                    lg: 12,
                    md: 12,
                    xs: 12,
                }}
            >
                <Box className={CAN.inputComposite}>
                    <SCUITextFieldWithSelectBox
                        placeholder={' '}
                        selectList={[
                            { value: 1, text: t('general.phoneNumber') },
                            { value: 2, text: t('lcwMoney.email') },
                        ]}
                        onChangeSelect={(event: any) => {
                            setDocumentType(event.target.value as number);
                            setCustomer(null);
                        }}
                        selectValue={documentType}
                        onChangeTextField={(event: any) => {
                            setSearchedText(event.target.value.trim());
                        }}
                        onClickButton={() => handleSearchClick()}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearchClick();
                            }
                        }}
                    />
                </Box>
            </Grid>
            {grid ? (
                <Grid
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <Box className={CAN.mainGrid}>
                        <SCUICustomDataGrid
                            randomId
                            rows={customer && customer.customerId > 0 ? [customer] : []}
                            columns={[
                                { field: 'customerId', headerName: t('customerInformation.customerNo'), flex: 1 },
                                { field: 'platform', headerName: 'Platform', flex: 1 },
                                { field: 'name', headerName: t('customerInformation.name'), flex: 1 },
                                { field: 'surname', headerName: t('customerInformation.surname'), flex: 1 },
                                {
                                    field: 'email',
                                    headerName: 'Email',
                                    flex: 1,
                                    renderCell: (params: any) => (
                                        <div>
                                            <Tooltip title={params.row.email}>
                                                <p>{params.row.email}</p>
                                            </Tooltip>
                                        </div>
                                    ),
                                },
                                { field: 'phoneNumber', headerName: t('general.phone'), flex: 1 },
                                {
                                    field: 'birthDate',
                                    headerName: t('customerInformation.birthday'),
                                    flex: 1,
                                    renderCell: (params: any) => (
                                        <div>
                                            <Tooltip title={params.row.birthDate}>
                                                <p>
                                                    {params.row.birthDate === '' ||
                                                    params.row.birthDate === null ||
                                                    params.row.birthDate === ' - '
                                                        ? ' - '
                                                        : TimeHelper.formatDate(params.row.birthDate)}
                                                </p>
                                            </Tooltip>
                                        </div>
                                    ),
                                },
                                {
                                    field: 'lastLoginDate',
                                    headerName: t('customerInformation.lastLoginDate'),
                                    flex: 1,
                                    renderCell: (params: any) => (
                                        <div>
                                            <Tooltip title={params.row.lastLoginDate}>
                                                <p>
                                                    {params.row.lastLoginDate === '' ||
                                                    params.row.lastLoginDate === null ||
                                                    params.row.lastLoginDate === ' - '
                                                        ? ' - '
                                                        : TimeHelper.formatDateHourSecond(params.row.lastLoginDate)}
                                                </p>
                                            </Tooltip>
                                        </div>
                                    ),
                                },
                                {
                                    field: 'anonymize',
                                    headerName: '',
                                    flex: 1,
                                    renderCell: () => (
                                        <div>
                                            <Tooltip title={t('customerInformation.anonymize')}>
                                                <p
                                                    className={canCls('cursorPointer', CAN.mainAnonymizeAction)}
                                                    style={{ color: 'rgb(0, 86, 179)' }}
                                                    onClick={() => UpdateAnonymization()}
                                                >
                                                    <b>{t('customerInformation.anonymize')}</b>
                                                </p>
                                            </Tooltip>
                                        </div>
                                    ),
                                },
                            ]}
                            pageSize={pageSize}
                            onPageSizeChange={(i: number) => {
                                setPageSize(i);
                            }}
                        />
                    </Box>
                </Grid>
            ) : (
                ''
            )}
        </SCUIContent>
    );
};
